/** Help centre hooks. Every one is scoped to the current organization and idle without one. */
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

import { useOrganization } from "@/auth/organization";

import { helpApi, helpKeys, type HelpArticle, type HelpFeedbackInput } from "./help";
import { ApiError } from "./http";

/** Articles change rarely, so a screen's help hint doesn't refetch on every focus. */
const STALE_MS = 5 * 60_000;

/** The articles this person can see, narrowed by a search text and a category. */
export function useHelpArticles(q: string, category?: string) {
  const { orgId } = useOrganization();
  const search = q.trim();
  return useQuery({
    queryKey: helpKeys.articles(orgId, search, category),
    queryFn: () => helpApi.articles(orgId, { q: search, category }),
    enabled: Boolean(orgId),
    staleTime: STALE_MS,
    // keep the list on screen while the next search loads
    placeholderData: keepPreviousData,
  });
}

export function useHelpArticle(slug: string | undefined) {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: helpKeys.article(orgId, slug ?? ""),
    queryFn: () => helpApi.article(orgId, slug!),
    enabled: Boolean(orgId && slug),
    staleTime: STALE_MS,
  });
}

/** The article that answers a help key, or `null` when there is none (a 404 is not an error here). */
export function useHelpContext(key: string | null | undefined) {
  const { orgId } = useOrganization();
  const normalized = key?.toLowerCase() ?? "";
  return useQuery({
    queryKey: helpKeys.context(orgId, normalized),
    queryFn: async (): Promise<HelpArticle | null> => {
      try {
        return await helpApi.context(orgId, normalized);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: Boolean(orgId && normalized),
    staleTime: STALE_MS,
    // a failed lookup has no data, so it would count as stale and be asked again on every focus
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** "Was this helpful?": one answer per person and article, so sending again replaces the last. */
export function useHelpFeedback() {
  const { orgId } = useOrganization();
  return useMutation({
    mutationFn: ({ slug, ...body }: HelpFeedbackInput & { slug: string }) =>
      helpApi.feedback(orgId, slug, body),
  });
}
